import { Helmet } from 'react-helmet-async';
import { toast } from '../components/Toast';
import { useAuthContext } from '../context/AuthContext';
import { ENDPOINTS, canonicalUrl } from '../config';

// Shown until the SEO Manager fetch resolves (and if it's ever unreachable) —
// SEO Manager (/admin/seo-manager, page "/login/") is the actual source of
// truth once a record exists there; this is only the pre-fetch/offline fallback.
const SEO_FALLBACK = {
    metaTitle: 'Login | FoneXpress Kenya',
    metaDescription:
        'Sign in to your FoneXpress Kenya account to track orders, manage your wishlist, and check out faster.',
    robotsTag: 'noindex, nofollow',
};

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthContext();

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [seo, setSeo] = useState(SEO_FALLBACK);

    useEffect(() => {
        fetch(ENDPOINTS.SEO.BY_URL('/login'))
            .then((res) => res.json())
            .then((data) => {
                const f = data?.isOk ? data.data : null;
                if (!f) return;
                setSeo({
                    metaTitle: f.metaTitle?.trim() || SEO_FALLBACK.metaTitle,
                    metaDescription: f.metaDescription?.trim() || SEO_FALLBACK.metaDescription,
                    robotsTag: f.robotsTag?.trim() || SEO_FALLBACK.robotsTag,
                });
            })
            .catch(() => {
                /* keep SEO_FALLBACK */
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const result = await login(formData.email, formData.password);
            if (result.isOk) {
                toast.success('Signed in successfully');
                const redirectTo = location.state?.from?.pathname || '/';
                navigate(redirectTo, { replace: true });
            } else {
                toast.error(result.message || 'Invalid email or password');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center px-4 pt-[10px] pb-16">
            <Helmet>
                <title>{seo.metaTitle}</title>
                <meta name="description" content={seo.metaDescription} />
                <link rel="canonical" href={canonicalUrl('/login')} />
                <meta name="robots" content={seo.robotsTag} />
            </Helmet>
            <div className="w-full max-w-md">
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-foreground mb-2">Welcome Back</h1>
                        <p className="text-muted-foreground text-sm">Sign in to your FoneXpress account</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                    errors.email
                                        ? 'border-red-500 focus:border-red-500'
                                        : 'border-border focus:border-primary'
                                }`}
                            />
                            {errors.email && (
                                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Enter your password"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.password
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                            )}
                        </div>

                        {/* Forgot Password */}
                        <div className="text-right">
                            <Link
                                to="/forgot-password"
                                className="text-sm text-primary hover:underline"
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting && (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {submitting ? 'Signing In...' : 'Sign In'}
                        </button>
                    </form>

                    {/* Register Link */}
                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-primary font-medium hover:underline">
                            Create Account
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
